import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import { ProposalStage } from "generated/graphql";
import useUpdateStageProductsOrdering from "hooks/proposals/useUpdateStageProductsOrdering";
import { useCallback, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { FormProvider, useForm } from "react-hook-form";
import { draftColors } from "./draftDesignTokens";
import {
  buildMutationInput,
  buildStageState,
  GroupState,
  StageItem,
  StageOrderingState,
  validateStageState,
} from "./stageOrderingState";
import ArrangeProductItem from "./ArrangeProductItem";
import ArrangeGroupItem, { groupDroppableId } from "./ArrangeGroupItem";

type ArrangeProductsDrawerProps = {
  open: boolean;
  onClose: () => void;
  stage: ProposalStage;
};

const stageDroppableId = "stage-items";

function parseGroupDroppableIndex(droppableId: string): number | null {
  const match = droppableId.match(/^group-(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}

function dissolveGroupIfNeeded(items: StageItem[]): StageItem[] {
  return items.flatMap((item) => {
    if (item.kind === "group" && item.group.products.length === 0) {
      return [];
    }
    if (item.kind === "group" && item.group.products.length === 1) {
      const product = item.group.products[0];
      return [{ kind: "product" as const, product }];
    }
    return [item];
  });
}

export default function ArrangeProductsDrawer({
  open,
  onClose,
  stage,
}: ArrangeProductsDrawerProps) {
  const { sendRequest } = useUpdateStageProductsOrdering();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());

  const defaultValues = useMemo(() => buildStageState(stage), [stage]);

  const methods = useForm<StageOrderingState>({
    defaultValues,
    values: open ? defaultValues : undefined,
  });

  const { watch, setValue, handleSubmit, setError, formState } = methods;
  const items = watch("items");

  const toggleGroupCollapse = useCallback((index: number) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, combine } = result;
      if (!destination && !combine) return;

      const currentItems = [...methods.getValues("items")];

      if (combine) {
        const sourceIsStage = source.droppableId === stageDroppableId;
        if (!sourceIsStage) return;

        const sourceItem = currentItems[source.index];
        const targetItem = currentItems.find(
          (item, idx) =>
            (item.kind === "product" && item.product.id === combine.draggableId) ||
            (item.kind === "group" && (item.group.id ?? `new-group-${idx}`) === combine.draggableId)
        );

        if (!targetItem) return;

        if (sourceItem.kind === "product" && targetItem.kind === "product") {
          const newGroup: GroupState = {
            id: null,
            name: "",
            isOptional: false,
            showIndividualProducts: false,
            products: [targetItem.product, sourceItem.product],
          };
          const targetIndex = currentItems.indexOf(targetItem);
          const newItems = currentItems.filter((_, i) => i !== source.index && i !== targetIndex);
          newItems.splice(Math.min(source.index, targetIndex), 0, {
            kind: "group",
            group: newGroup,
          });
          setValue("items", newItems, { shouldDirty: true });
          return;
        }

        if (sourceItem.kind === "product" && targetItem.kind === "group") {
          const newItems = currentItems.filter((_, i) => i !== source.index);
          const groupIdx = newItems.findIndex((item) => item === targetItem);
          if (groupIdx !== -1 && newItems[groupIdx].kind === "group") {
            const group = (newItems[groupIdx] as { kind: "group"; group: GroupState }).group;
            newItems[groupIdx] = {
              kind: "group",
              group: {
                ...group,
                products: [...group.products, sourceItem.product],
              },
            };
          }
          setValue("items", newItems, { shouldDirty: true });
          return;
        }

        return;
      }

      const sourceIsStage = source.droppableId === stageDroppableId;
      const destIsStage = destination!.droppableId === stageDroppableId;
      const sourceGroupIdx = parseGroupDroppableIndex(source.droppableId);
      const destGroupIdx = parseGroupDroppableIndex(destination!.droppableId);

      // Reorder within stage (top-level)
      if (sourceIsStage && destIsStage) {
        const [moved] = currentItems.splice(source.index, 1);
        currentItems.splice(destination!.index, 0, moved);
        setValue("items", currentItems, { shouldDirty: true });
        return;
      }

      // Reorder within a group
      if (sourceGroupIdx !== null && destGroupIdx !== null && sourceGroupIdx === destGroupIdx) {
        const groupItem = currentItems[sourceGroupIdx];
        if (groupItem.kind !== "group") return;
        const products = [...groupItem.group.products];
        const [moved] = products.splice(source.index, 1);
        products.splice(destination!.index, 0, moved);
        currentItems[sourceGroupIdx] = {
          kind: "group",
          group: { ...groupItem.group, products },
        };
        setValue("items", currentItems, { shouldDirty: true });
        return;
      }

      // Drag product out of group to stage level
      if (sourceGroupIdx !== null && destIsStage) {
        const groupItem = currentItems[sourceGroupIdx];
        if (groupItem.kind !== "group") return;
        const products = [...groupItem.group.products];
        const [movedProduct] = products.splice(source.index, 1);
        currentItems[sourceGroupIdx] = {
          kind: "group",
          group: { ...groupItem.group, products },
        };
        const newItems = dissolveGroupIfNeeded(currentItems);
        newItems.splice(destination!.index, 0, {
          kind: "product",
          product: movedProduct,
        });
        setValue("items", newItems, { shouldDirty: true });
        return;
      }

      // Drag product from stage into a group
      if (sourceIsStage && destGroupIdx !== null) {
        const sourceItem = currentItems[source.index];
        if (sourceItem.kind !== "product") return;
        const newItems = currentItems.filter((_, i) => i !== source.index);
        let adjustedGroupIdx = destGroupIdx;
        if (source.index < destGroupIdx) adjustedGroupIdx--;
        const groupItem = newItems[adjustedGroupIdx];
        if (!groupItem || groupItem.kind !== "group") return;
        const products = [...groupItem.group.products];
        products.splice(destination!.index, 0, sourceItem.product);
        newItems[adjustedGroupIdx] = {
          kind: "group",
          group: { ...groupItem.group, products },
        };
        setValue("items", newItems, { shouldDirty: true });
        return;
      }

      // Drag product from one group to another
      if (sourceGroupIdx !== null && destGroupIdx !== null && sourceGroupIdx !== destGroupIdx) {
        const srcGroup = currentItems[sourceGroupIdx];
        const dstGroup = currentItems[destGroupIdx];
        if (srcGroup.kind !== "group" || dstGroup.kind !== "group") return;
        const srcProducts = [...srcGroup.group.products];
        const [movedProduct] = srcProducts.splice(source.index, 1);
        const dstProducts = [...dstGroup.group.products];
        dstProducts.splice(destination!.index, 0, movedProduct);
        currentItems[sourceGroupIdx] = {
          kind: "group",
          group: { ...srcGroup.group, products: srcProducts },
        };
        currentItems[destGroupIdx] = {
          kind: "group",
          group: { ...dstGroup.group, products: dstProducts },
        };
        setValue("items", dissolveGroupIfNeeded(currentItems), { shouldDirty: true });
      }
    },
    [methods, setValue]
  );

  const onSubmit = useCallback(
    (data: StageOrderingState) => {
      const validationError = validateStageState(data);
      if (validationError) {
        setError("items", { message: validationError });
        return;
      }
      const input = buildMutationInput(data);
      sendRequest(input);
      onClose();
    },
    [sendRequest, onClose, setError]
  );

  return (
    <UiSideModal open={open} onClose={onClose} title="Arrange Products" width="498px">
      <FormProvider {...methods}>
        <MDBox p="24px" display="flex" flexDirection="column" gap="8px">
          {formState.errors.items?.message && (
            <UiTypography uiVariant="body-sm" sx={{ color: draftColors.red, mb: "8px" }}>
              {formState.errors.items.message}
            </UiTypography>
          )}

          <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary, mb: "8px" }}>
            Drag products to reorder. Drop a product onto another to create a group.
          </UiTypography>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={stageDroppableId} isCombineEnabled>
              {(provided) => (
                <MDBox
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    minHeight: "100px",
                  }}
                >
                  {items.map((item, index) => {
                    const draggableId =
                      item.kind === "product"
                        ? item.product.id
                        : item.group.id ?? `new-group-${index}`;

                    return (
                      <Draggable key={draggableId} draggableId={draggableId} index={index}>
                        {(provided, snapshot) => (
                          <MDBox
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              backgroundColor: snapshot.isDragging
                                ? draftColors.chipBg
                                : draftColors.surface,
                              borderRadius: "8px",
                              border: `1px solid ${draftColors.border}`,
                              ...(snapshot.combineTargetFor
                                ? {
                                    border: `2px solid ${draftColors.blue}`,
                                    backgroundColor: draftColors.chipBg,
                                  }
                                : {}),
                            }}
                          >
                            {item.kind === "product" ? (
                              <ArrangeProductItem
                                product={item.product}
                                dragHandleProps={provided.dragHandleProps}
                              />
                            ) : (
                              <ArrangeGroupItem
                                groupIndex={index}
                                collapsed={collapsedGroups.has(index)}
                                onToggleCollapse={() => toggleGroupCollapse(index)}
                                dragHandleProps={provided.dragHandleProps}
                              />
                            )}
                          </MDBox>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </MDBox>
              )}
            </Droppable>
          </DragDropContext>

          <UiButton
            uiVariant="primary-blue"
            onClick={handleSubmit(onSubmit)}
            sx={{ alignSelf: "flex-end", mt: "8px" }}
          >
            Save Order
          </UiButton>
        </MDBox>
      </FormProvider>
    </UiSideModal>
  );
}
